import {
  CellTemplate,
  Compatible,
  Uncertain,
  UncertainCompatible,
  getCharFromKeyCode,
  isAlphaNumericKey,
  isNavigationKey,
  keyCodes,
} from "@silevis/reactgrid";
import { ReactNode } from "react";
import { CellCustomType } from "@lhd/components/CloneApp/pages/SheetApp/type";
import TextTooltip from "@lhd/components/TextToolTip";

export class TextCellTemplate implements CellTemplate<CellCustomType> {
  private isWrapText = false;
  constructor(_isWrapText?: boolean) {
    this.isWrapText = !!_isWrapText;
  }
  getCompatibleCell(
    uncertainCell: Uncertain<CellCustomType>,
  ): Compatible<CellCustomType> {
    return {
      value: NaN,
      ...(uncertainCell as CellCustomType),
    };
  }

  handleKeyDown(
    cell: Compatible<CellCustomType>,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean,
  ): { cell: Compatible<CellCustomType>; enableEditMode: boolean } {
    if (cell.nonEditable) {
      return {
        cell,
        enableEditMode: false,
      };
    }
    const char = getCharFromKeyCode(keyCode, shift);
    const isIME = keyCode === 229 || keyCode === 299;
    if (isIME) {
      return {
        cell,
        enableEditMode: true,
      };
    }
    if (
      ctrl ||
      alt ||
      !isAlphaNumericKey(keyCode) ||
      (shift && keyCode === keyCodes.SPACE)
    ) {
      return {
        cell,
        enableEditMode:
          keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER,
      };
    }
    cell.onChange?.();
    return {
      cell: {
        ...cell,
        text: cell.text + (shift ? char : char.toLowerCase()),
      },
      enableEditMode: true,
    };
  }

  getClassName(cell: Compatible<CellCustomType>): string {
    return cell.className || "";
  }

  update(
    cell: Compatible<CellCustomType>,
    cellToMerge: UncertainCompatible<CellCustomType>,
  ): Compatible<CellCustomType> {
    return this.getCompatibleCell({
      ...cell,
      ...cellToMerge,
    });
  }

  render(
    cell: Compatible<CellCustomType>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<CellCustomType>, commit: boolean) => void,
  ): ReactNode {
    if (!isInEditMode) {
      if (this.isWrapText) {
        return (
          <div
            style={{
              wordBreak: "break-word",
              whiteSpace: "break-spaces",
              width: "100%",
            }}
          >
            {cell.text}
          </div>
        );
      }
      return <div>{cell.text}</div>;
    }
    return (
      <input
        disabled={!!cell.nonEditable}
        ref={(ref) => {
          if (ref) {
            setTimeout(() => {
              ref.focus();
            }, 0);
          }
        }}
        defaultValue={cell.text}
        style={{
          width: "100%",
          outline: "none",
          resize: "none",
          border: "1px solid #fff",
          fontSize: "inherit",
          fontFamily: "inherit",
          background: "transparent",
          boxSizing: "border-box",
          backgroundColor: "#fff",
        }}
        onChange={(e) => {
          cell.onChange?.();
          onCellChanged(
            this.getCompatibleCell({
              ...cell,
              text: e.currentTarget.value.trim(),
            }),
            false,
          );
        }}
        onBlur={(e) =>
          onCellChanged(
            this.getCompatibleCell({
              ...cell,
              text: e.currentTarget.value.trim(),
            }),
            true,
          )
        }
        onKeyDown={(e) => {
          if (
            isAlphaNumericKey(e.keyCode) ||
            isNavigationKey(e.keyCode) ||
            (e.key === "Enter" && e.shiftKey)
          ) {
            e.stopPropagation();
          }
        }}
        onFocus={(e) => {
          const input = e.target;
          input.setSelectionRange(input.value.length, input.value.length);
        }}
        onCopy={(e) => e.stopPropagation()}
        onCut={(e) => e.stopPropagation()}
        onPaste={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      />
    );
  }
}
